/**
 * State-surface validation for FmFormFlowPage descriptors: the typed payload each
 * non-ready state must carry, the actions those surfaces may name, and the paid
 * boundary's previewability rules.
 *
 * Split from `FmFormFlowPage.validation.ts` so neither file crosses the 500-line
 * gate.
 */
import {
  exactFormFlowKeys as exact,
  failFormFlow as fail,
  formFlowArray as array,
  formFlowObject as object,
  formFlowText as text,
} from './FmFormFlowValidation.shared';
import { assertFmUnlockPreviewDescriptor } from './FmUnlockPreview.validation';

/**
 * An action a state surface names must be declared, so the surface can never be
 * a dead end. Mirrors `requireResolvingControl` on the operational workspace
 */
export function requireDeclaredAction(
  actionIdValue: unknown,
  path: string,
  actions: Record<string, unknown>[],
): void {
  const actionId = text(actionIdValue, path);
  const action = actions.find((item) => String(item.id) === actionId);
  if (!action) fail(path, 'must reference a declared action');
  if (action?.disabled === true) fail(path, 'must reference an action that is not disabled');
  /* And never a confirmation-gated one. EVERY state surface renders its action
     through a confirmation-less sink — `StateAction` has no `FmConfirm` wrapper
     and `FmUnlockPreview` calls `onEvent` directly — while `FlowActions`, the one
     renderer that does wrap, is exactly where `stateSurfaceActionIds` removes
     these ids from so the control is not said twice. So on a locked, empty or
     failed descriptor a confirmation-gated action exists on screen ONLY in its
     confirmation-less form, and on a paid boundary that is the unlock CTA.
     The rule lives HERE, not at one call site: it covers the stage anchor,
     `empty.primaryActionId`, `error.retryActionId` and
     `locked.unlock.unlockActionId` alike.
     `FmOperationalWorkspaceState.validation.ts` already refuses `confirmationId`
     on every resolving control; this is the same contract. */
  if (action?.confirmation !== undefined) {
    fail(path, 'must not reference a confirmation-gated action');
  }
}

/**
 * The typed surface each non-ready state carries. Without this a locked, empty
 * or failed flow renders as one grey sentence in a dead band with nothing to do
 */
const STATE_PAYLOAD_KEYS = ['empty', 'error', 'locked'] as const;

export function validateFormFlowStateSurface(
  descriptor: Record<string, unknown>,
  actions: Record<string, unknown>[],
): void {
  const state = String(descriptor.state);
  /* Exactly the payload this state owns, and nothing else. A `ready` descriptor
     carrying a stale `locked` object, or an `error` one that also kept `empty`,
     is a descriptor whose state and content disagree — the renderer would show
     one and the next state change would show the other. */
  const present = STATE_PAYLOAD_KEYS.filter((key) => descriptor[key] !== undefined);
  const expected = STATE_PAYLOAD_KEYS.filter((key) => key === state);
  if (present.length !== expected.length || present.some((key, index) => key !== expected[index])) {
    fail(
      'descriptor',
      expected.length
        ? `must carry exactly the '${state}' state surface`
        : `must not carry a state surface while state is '${state}'`,
    );
  }
  if (state === 'empty') {
    const empty = object(descriptor.empty, 'descriptor.empty');
    exact(empty, new Set(['description', 'primaryActionId']), 'descriptor.empty');
    text(empty.description, 'descriptor.empty.description');
    requireDeclaredAction(empty.primaryActionId, 'descriptor.empty.primaryActionId', actions);
    return;
  }
  if (state === 'error') {
    const error = object(descriptor.error, 'descriptor.error');
    exact(error, new Set(['scopeLabel', 'description', 'retryActionId']), 'descriptor.error');
    text(error.scopeLabel, 'descriptor.error.scopeLabel');
    text(error.description, 'descriptor.error.description');
    requireDeclaredAction(error.retryActionId, 'descriptor.error.retryActionId', actions);
    return;
  }
  if (state !== 'locked') return;
  const locked = object(descriptor.locked, 'descriptor.locked');
  exact(locked, new Set(['title', 'description', 'unlock']), 'descriptor.locked');
  text(locked.title, 'descriptor.locked.title');
  text(locked.description, 'descriptor.locked.description');
  if (locked.unlock === undefined) return;
  const unlock = object(locked.unlock, 'descriptor.locked.unlock');
  exact(
    unlock,
    new Set(['title', 'summary', 'priceLabel', 'previewNote', 'unlockActionId', 'unlockLabel']),
    'descriptor.locked.unlock',
  );
  /* The adapter owns its own copy rules, so every archetype that adopts it gets
     the same ones. */
  assertFmUnlockPreviewDescriptor(unlock, 'descriptor.locked.unlock', fail);
  requireDeclaredAction(unlock.unlockActionId, 'descriptor.locked.unlock.unlockActionId', actions);
  const navigation = object(descriptor.navigation, 'descriptor.navigation');
  const items = array(navigation.items, 'descriptor.navigation.items').map((item, index) =>
    object(item, `descriptor.navigation.items[${index}]`),
  );
  const activeItem = items.find((item) => item.id === navigation.activeId);
  refusePreviewedControls(descriptor, previewedBlockIds(activeItem));
}

/**
 * The ids of the blocks a previewed paid boundary actually renders. Only the
 * ACTIVE item's blocks reach the screen, so only those are checked: refusing a
 * map that lives on some other stage would reject a legitimate flow.
 */
function previewedBlockIds(item: Record<string, unknown> | undefined): ReadonlySet<string> {
  if (!item) return new Set<string>();
  const ordered =
    item.story !== undefined
      ? (object(item.story, 'descriptor.navigation.activeItem.story').mobileOrder as string[])
      : (item.blockIds as string[]);
  return new Set(ordered ?? []);
}

/**
 * A previewed paid boundary keeps its blocks on screen, made non-operable by
 * `fieldset disabled`. That reaches NATIVE form controls and nothing else, so a
 * block or control kind it cannot disable may not sit behind the lock — and a
 * one-time secret must never be previewed to somebody who has not paid for it.
 *
 * `files` renders a dropzone div, `select` a div-based combobox, and `date` an
 * AntD picker whose trigger is a div opening a PORTALLED panel outside the
 * fieldset entirely — none of which a disabled fieldset reaches, so behind a
 * paid boundary they stay clickable, focusable and droppable.
 *
 * ONE predicate, checked at every level a control can enter the preview: a
 * field, and an editable-collection cell editor. The same FmSelect renders
 * behind both, so refusing it in one place and admitting it in the other is not
 * a boundary at all.
 *
 * It is an ALLOWLIST, not a denylist, so a kind added later is refused until
 * somebody checks what it renders.
 *
 * Exported because a STAGE-level lock previews its blocks the same way a
 * flow-level one does — `stageAllowsControls` refuses `locked` — and checking
 * only the flow-level lock would leave that door open.
 */
export function refusePreviewedControls(
  descriptor: Record<string, unknown>,
  previewedIds: ReadonlySet<string>,
): void {
  const previewable = new Set(['fields', 'collection', 'notice']);
  /* `collection` resolves to FOUR different renderers, so the type-level
     allowlist alone is one level too coarse — the same
     shape of hole `select` opens at field level.
     - `records` renders `<button role="radio">` — native, disabled.
     - `edit`    renders the cell editors, checked kind by kind below.
     - `review`  renders FmTable with no `onRowSelect`, no `onClick` and no
                 expandable rows, and FmTable makes a row focusable ONLY for
                 those (`isRowInteractive`, FmTable.datarow.tsx) — so its rows
                 carry no tabIndex and no handler, and there is nothing behind
                 the lock to operate.
     - `evidence` is REFUSED because nobody has checked it, which is what an
                 allowlist means. FmEvidence renders FmButton, and FmButton
                 itself renders `<a href>` when given an href; an anchor is
                 not disabled by a fieldset, so allowing it needs a real audit
                 rather than an assumption.
     An allowlist, so a mode added later is refused until somebody checks what
     it renders. */
  const previewableCollection = new Set(['records', 'review', 'edit']);
  const nativelyDisabled = (kind: string): boolean =>
    ['text', 'number', 'choice', 'boolean'].includes(kind);
  array(descriptor.blocks, 'descriptor.blocks').forEach((value, index) => {
    const block = object(value, `descriptor.blocks[${index}]`);
    if (!previewedIds.has(String(block.id))) return;
    const control = object(block.control, `descriptor.blocks[${index}].control`);
    if (!previewable.has(String(control.type))) {
      fail(
        `descriptor.blocks[${index}].control.type`,
        `cannot be previewed behind a paid boundary: '${String(control.type)}' is not disabled by the block region`,
      );
    }
    if (control.type === 'fields') {
      array(control.fields, `descriptor.blocks[${index}].control.fields`).forEach(
        (fieldValue, fieldIndex) => {
          const field = object(
            fieldValue,
            `descriptor.blocks[${index}].control.fields[${fieldIndex}]`,
          );
          if (!nativelyDisabled(String(field.kind))) {
            fail(
              `descriptor.blocks[${index}].control.fields[${fieldIndex}].kind`,
              `cannot be previewed behind a paid boundary: '${String(field.kind)}' is not disabled by the block region`,
            );
          }
        },
      );
      return;
    }
    if (control.type === 'collection' && !previewableCollection.has(String(control.mode))) {
      fail(
        `descriptor.blocks[${index}].control.mode`,
        `cannot be previewed behind a paid boundary: '${String(control.mode)}' is not disabled by the block region`,
      );
    }
    if (control.type !== 'collection' || control.mode !== 'edit') return;
    array(control.columns, `descriptor.blocks[${index}].control.columns`).forEach(
      (columnValue, columnIndex) => {
        const columnPath = `descriptor.blocks[${index}].control.columns[${columnIndex}]`;
        const column = object(columnValue, columnPath);
        if (column.editor === undefined) return;
        const editor = object(column.editor, `${columnPath}.editor`);
        if (!nativelyDisabled(String(editor.kind))) {
          fail(
            `${columnPath}.editor.kind`,
            `cannot be previewed behind a paid boundary: '${String(editor.kind)}' is not disabled by the block region`,
          );
        }
      },
    );
  });
}
